import React, { useState } from "react";
import { CheckCircle2, Loader2, Plus, Workflow, Zap } from "lucide-react";
import ActiveCardShell from "../ActiveCardShell";
import { sleep, RunButton, useAlive } from "../phase2/shared";

const BLOCKS = [
  { kind: "WHEN", label: "Merchant approved" },
  { kind: "THEN", label: "Ship the welcome kit" },
  { kind: "THEN", label: "Alert the agent on Slack" },
  { kind: "THEN", label: "Schedule 30-day review" },
];

// Click blocks to assemble a when-then flow, then test-run it node by node.
export default function CustomWorkflowCard() {
  const [flow, setFlow] = useState([]);
  const [lit, setLit] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);
  const alive = useAlive();

  const add = (i) => { if (!flow.includes(i)) { setFlow((f) => [...f, i]); setLive(false); } };
  const testRun = async (nodes) => {
    for (let i = 0; i < nodes.length; i += 1) { setLit(i); await sleep(420); if (!alive.current) return; }
    setLive(true);
  };
  const run = async () => {
    setBusy(true); setFlow([]); setLit(-1); setLive(false);
    const nodes = [];
    for (let i = 0; i < BLOCKS.length; i += 1) { await sleep(450); if (!alive.current) return; nodes.push(i); setFlow([...nodes]); }
    await sleep(300);
    await testRun(nodes);
    setBusy(false);
  };

  return (
    <ActiveCardShell id="T7100cr" kicker="CUSTOM BUILD" title="Custom Workflow Builder" Icon={Workflow}
      actions={<RunButton tid="veroogle-t7100-run" running={busy} onClick={run} Icon={busy ? Loader2 : Zap} label="Build flow" />}>
      <div className="flex flex-wrap gap-2 mb-4">
        {BLOCKS.map((b, i) => (
          <button key={b.label} type="button" onClick={() => add(i)} disabled={busy || flow.includes(i)} data-testid={`veroogle-t7100-block-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-slate-300 hover:border-brand-cyan/50 hover:text-white transition-colors disabled:opacity-35">
            <Plus className="w-3 h-3" /><span className={b.kind === "WHEN" ? "text-amber-300 font-mono" : "text-brand-cyan font-mono"}>{b.kind}</span>{b.label}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-dashed border-white/15 bg-navy-950/50 p-3 min-h-[76px] flex flex-wrap items-center gap-2" data-testid="veroogle-t7100-canvas">
        {flow.length === 0 && <span className="text-xs text-slate-500">Click blocks to snap a flow together…</span>}
        {flow.map((bi, i) => (
          <React.Fragment key={bi}>
            {i > 0 && <span className="text-slate-600">→</span>}
            <span data-testid={`veroogle-t7100-node-${i}`}
              className={`rounded-lg border px-3 py-1.5 text-xs animate-in zoom-in-90 fade-in duration-300 transition-colors ${lit >= i ? "border-emerald-400/60 bg-emerald-500/15 text-white shadow-[0_0_14px_rgba(16,185,129,0.35)]" : "border-white/15 bg-white/[0.04] text-slate-300"}`}>
              <b className="font-mono mr-1">{BLOCKS[bi].kind}</b>{BLOCKS[bi].label}
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {!busy && flow.length > 1 && !live && (
          <button type="button" onClick={() => testRun(flow)} data-testid="veroogle-t7100-test"
            className="rounded-lg border border-brand-cyan/40 px-3 py-1.5 font-mono text-[11px] tracking-[0.14em] uppercase text-brand-cyan hover:bg-brand-cyan/10 transition-colors">Test run</button>
        )}
        {live && (
          <div data-testid="veroogle-t7100-outcome" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-navy-950 font-mono text-xs sm:text-sm font-bold tracking-[0.14em] uppercase shadow-[0_0_24px_rgba(16,185,129,0.4)] animate-in zoom-in-75 fade-in">
            <CheckCircle2 className="w-4 h-4" /> Flow live · {flow.length} steps · 0 code
          </div>
        )}
      </div>
    </ActiveCardShell>
  );
}
